import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  APIProviderContext,
  AdvancedMarker,
  Map as GoogleMap,
  useApiIsLoaded,
  useMap,
} from '@vis.gl/react-google-maps'

import { cn } from '@/lib/utils'
import { useStreetViewSwitch } from '@/components/maps/useStreetViewSwitch'
import { ViewSwitch } from '@/components/maps/ViewSwitch'
import { CameraPinSvg } from '@/components/map/CameraPinSvg'

// #247 — the bearing helper moved to lib/geo; kept exported here for its existing callers.
export { bearingDegrees } from '@/lib/geo'

// #243 — where the event happened: a satellite map by default, and a Map | Street View switch
// that turns that same box into Street View and back.

const MAP_INSTANCE_ID = 'event-location'
// Close enough to see the track, gate or hedge the camera is watching.
const ZOOM = 18
interface LatLng {
  lat: number
  lng: number
}

export function EventLocationMap({ lat, lng, className }: LatLng & { className?: string }) {
  const { t } = useTranslation('events')
  // MapsApiProvider mounts Google's APIProvider only once the map config has loaded with a
  // Google key. Without it <Map> throws, so render nothing at all — and leave the panel no
  // empty bordered gap.
  const mapsAvailable = useContext(APIProviderContext) !== null
  if (!mapsAvailable) return null

  return (
    <section
      aria-label={t('locationMap.label')}
      // The popup's ← → handler leaves keys from inside this box to the map (see EventDetailModal).
      data-event-location-map
      // Grows into the space above the download buttons; never smaller than a usable map, and
      // (#245) never taller than a 360px map: on a big monitor the panel has hundreds of pixels
      // to spare and the map became a tall, narrow slot that pulled the eye off the photo.
      className={cn(
        'flex min-h-[232px] max-h-[392px] flex-1 flex-col border-b border-border/60 px-5 py-4',
        className,
      )}
    >
      {/* Keyed on the location: a different event starts again on Map, with no stale note. */}
      <LocationBox key={`${lat},${lng}`} lat={lat} lng={lng} />
    </section>
  )
}

function LocationBox({ lat, lng }: LatLng) {
  const { t } = useTranslation('events')
  const apiReady = useApiIsLoaded()
  const map = useMap(MAP_INSTANCE_ID)
  // #247 — the Map | Street View logic lives in a hook shared with the camera modal.
  const sv = useStreetViewSwitch(map, { lat, lng })

  // The Google map is reused between events (reuseMaps), so it arrives in whatever state the
  // last event left it: put it back on this event, on the map, and leave it that way.
  useEffect(() => {
    if (!map) return
    const panorama = map.getStreetView()
    map.setCenter({ lat, lng })
    map.setZoom(ZOOM)
    // #326 — some sites have no satellite imagery at zoom 18 (grey tiles): open at the closest zoom that has imagery
    let cancelled = false
    const svc =
      typeof google !== 'undefined' && google.maps?.MaxZoomService ? new google.maps.MaxZoomService() : null
    // the typings return a Promise even in callback mode: swallow a rejection (no "Uncaught (in promise)")
    void svc
      ?.getMaxZoomAtLatLng({ lat, lng }, (r) => {
        if (cancelled || !r || r.status !== 'OK') return
        if (r.zoom < ZOOM) map.setZoom(r.zoom)
      })
      ?.catch(() => {})
    panorama.setVisible(false)
    return () => {
      cancelled = true
      sv.cancel()
      panorama.setVisible(false)
    }
  }, [map, lat, lng]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="relative min-h-[200px] flex-1 overflow-hidden rounded-lg border border-border bg-muted">
        {apiReady ? (
          <>
            <div className="absolute inset-0">
              <GoogleMap
                id={MAP_INSTANCE_ID}
                // One Google map per page load, reused for every event, instead of a new
                // (billed) map each time the popup opens.
                reuseMaps
                mapId="DETERCAM_MAP"
                mapTypeId="hybrid"
                defaultCenter={{ lat, lng }}
                defaultZoom={ZOOM}
                tilt={0}
                // The panel scrolls: the wheel and one finger stay with the page.
                gestureHandling="cooperative"
                disableDefaultUI
                zoomControl
                fullscreenControl
                clickableIcons={false}
                style={{ width: '100%', height: '100%' }}
              >
                {/* #282 — our own pin in the brand colour, not Google's default red pin */}
                <AdvancedMarker position={{ lat, lng }} title={t('locationMap.label')}>
                  <CameraPinSvg brand />
                </AdvancedMarker>
              </GoogleMap>
            </div>

            <ViewSwitch
              view={sv.view}
              checking={sv.checking}
              disabled={!sv.ready}
              onMap={sv.showMap}
              onStreetView={sv.showStreetView}
            />
          </>
        ) : (
          <div
            data-testid="event-map-placeholder"
            className="absolute inset-0 animate-pulse bg-muted motion-reduce:animate-none"
          />
        )}
      </div>

      {sv.unavailable && (
        <p role="status" className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {t('streetView.notAvailable')}{' '}
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-sm font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('streetView.openInGoogleMaps')}
          </a>
        </p>
      )}
    </>
  )
}
